import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetOutlets, dataSetPackLines, dataSetPrintServers, dataSetPrinters, dataSetReports, dataSetInventories } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { useHistory } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IOutlet, Outlet } from '../../../@types/model/masterData/outlet/outlet';
import OutletHttpService from '../../../services/http/masterData/outletHttpService';
import PackLineHttpService from '../../../services/http/masterData/packLineHttpService';
import { IOutletFormValues, OutletFormValues } from '../../../@types/model/masterData/outlet/outletFormValues';
import OutletForm from './form/OutletForm';
import { IApiError } from '../../../@types/other';
import PrintServerHttpService from '../../../services/http/masterData/printServerHttpService';
import PrinterHttpService from '../../../services/http/masterData/printerHttpService';
import ReportHttpService from '../../../services/http/masterData/reportHttpService';
import InventoryHttpService from '../../../services/http/masterData/inventoryHttpService';
import { setOutletMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const OutletScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const outlets = useAppSelector(x => x.masterData.outlets);
    const packLines = useAppSelector(x => x.masterData.packLines);
    const sites = useAppSelector(x => x.masterData.sites);
    const printServers = useAppSelector(x => x.masterData.printServers);
    const printers = useAppSelector(x => x.masterData.printers);
    const reports = useAppSelector(x => x.masterData.reports);
    const inventories = useAppSelector(x => x.masterData.inventories);
    const auth = useAppSelector(x => x.auth);
    const pathname = useHistory()?.location?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedOutlet, setSelectedOutlet] = useState<IOutlet>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingOutlet, setDeletingOutlet] = useState<IOutlet>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res1 = await OutletHttpService.getOutletData();
                    const outletData = res1.data;
                    if (outletData) {
                        const res2 = await PackLineHttpService.getPackLineData();
                        const res3 = await PrintServerHttpService.getPrintServerData();
                        const res4 = await PrinterHttpService.getPrinterData();
                        const res5 = await ReportHttpService.getReportData();
                        const res6 = await InventoryHttpService.getInventoryData();

                        dispatch(dataSetPackLines(res2.data));
                        dispatch(dataSetPrintServers(res3.data));
                        dispatch(dataSetPrinters(res4.data));
                        dispatch(dataSetReports(res5.data));
                        dispatch(dataSetInventories(res6.data));
                        dispatch(dataSetOutlets(outletData));
                    }
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading outlets.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = () => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedOutlet(undefined);
        setFormGuid(undefined);
    };

    const editOutlet = (outlet : IOutlet) => {
        setSelectedOutlet(outlet);
        setFormGuid(outlet.guid);
        openDialog();
    };

    const onSubmit = async (values : IOutletFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        payload.guid = formGuid ?? uuidv4();

        try {
            const res = await OutletHttpService.addOrUpdateOutlet(Outlet(payload));
            const updatedList = upsertArrayElement(outlets, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetOutlets(updatedList));
            await setOutletMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedOutlet ? 'Outlet updated successfully.' : 'Outlet added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating outlet data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = (outlet : IOutlet) => {
        setDeletingOutlet(outlet);
        setIsDeletePopupOpen(true);
    };

    const closeDeleteConfirmationPopup = () => {
        setDeletingOutlet(undefined);
        setIsDeletePopupOpen(false);
    };

    const removeOutlet = async () => {
        if (!deletingOutlet) return;
        const updatedOutlet = { ...deletingOutlet, isActive: false };
        setIsLoading(true);
        try {
            const res = await OutletHttpService.deleteOutlet(updatedOutlet.id);
            const updatedList = upsertArrayElement(outlets, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetOutlets(updatedList));
            await setOutletMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Outlet deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting outlet.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return OutletFormValues(
                selectedOutlet,
                packLines,
                printServers,
                printers,
                reports,
                sites,
                inventories
            );
        }
    }, [selectedOutlet, packLines, printServers, printers, reports, sites, inventories, isDialogOpen]);

    const getPackLineCode = useCallback((packLineId : number) => {
        const packLine = packLines?.find(x => x.id === packLineId);
        return packLine?.code ?? '';
    }, [packLines]);

    const getPrintServerName = useCallback((printServerId : number) => {
        const printServer = printServers?.find(x => x.id === printServerId);
        return printServer?.name ?? '';
    }, [printServers]);

    const getPrinterName = useCallback((printerId : number) => {
        const printer = printers?.find(x => x.id === printerId);
        return printer?.name ?? '';
    }, [printers]);

    const getReportName = useCallback((reportId : number) => {
        const report = reports?.find(x => x.id === reportId);
        return report?.name ?? '';
    }, [reports]);

    const getSiteShortDescription = useCallback((siteId : number) => {
        const site = sites?.find(x => x.id === siteId);
        return site?.shortDescription ?? '';
    }, [sites]);

    const getInventoryDescription = useCallback((inventoryId : number) => {
        const inventory = inventories?.find(x => x.id === inventoryId);
        return inventory?.description ?? '';
    }, [inventories]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(r => pathname.includes(r.url) && r.isActive && r.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Outlet'
                    isEdit={!!selectedOutlet}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <OutletForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IOutlet>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editOutlet}
                        enableEditing={hasEditingRight}
                        enableDeleting={o => o.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Pack Line', field: 'packLineId', formatFunction: getPackLineCode, enableFiltering: true, enableSorting: true },
                            { title: 'Print Server', field: 'defaultPrintServerId', formatFunction: getPrintServerName, enableFiltering: true, enableSorting: true },
                            { title: 'Printer', field: 'defaultPrinterId', formatFunction: getPrinterName, enableFiltering: true, enableSorting: true },
                            { title: 'Report', field: 'defaultReportId', formatFunction: getReportName, enableFiltering: true, enableSorting: true },
                            { title: 'Site', field: 'defaultExporterSiteId', formatFunction: getSiteShortDescription, enableFiltering: true, enableSorting: true },
                            { title: 'Inventory', field: 'defaultInventoryId', formatFunction: getInventoryDescription, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={outlets}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Outlet'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this outlet?'
                onAccept={removeOutlet}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default OutletScreen;
